import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GamificationAchievement, GamificationBootstrap } from '../models/gamification-bootstrap.model';
import { DailyQuizStatusResult } from '../../quizzes/models/daily-quiz-setup.model';

export interface GamificationRecalculationResult {
  ProcessedStudentCount: number;
  UpdatedDayCount: number;
  AwardedTierCount: number;
  ElapsedMilliseconds: number;
}

export interface DeleteDailyQuizResult {
  StudentId: number;
  DailyQuizId: number;
  DeletedQuizResultCount: number;
}

@Injectable({ providedIn: 'root' })
export class GamificationRepositoryService {
  private static readonly CACHE_DURATION_MS = 5 * 60 * 1000;
  private readonly gamificationApi: string;
  private readonly quizApi: string;
  private cachedBootstrap?: GamificationBootstrap;
  private cachedOverview?: GamificationBootstrap;
  private cachedAchievements?: GamificationAchievement[];
  private cachedDailyQuiz?: DailyQuizStatusResult;
  private cacheTime = 0;

  constructor(
    @Inject(DwConfigServiceToken) configService: DwConfigService,
    private readonly http: HttpClient,
    private readonly securityUserService: DwSecurityUserService
  ) {
    this.gamificationApi = `${configService.coreConfig.apiRoot}/aviator-online/gamification`;
    this.quizApi = `${configService.coreConfig.apiRoot}/aviator-online/quizzes`;
    this.securityUserService.securityContextChanged().subscribe(() => this.invalidate());
  }

  getBootstrap(): Observable<GamificationBootstrap> {
    if (this.isCacheValid() && this.cachedBootstrap) {
      return of(this.cachedBootstrap);
    }

    return this.http.get<GamificationBootstrap>(`${this.gamificationApi}/bootstrap`, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }
    }).pipe(tap(bootstrap => {
      this.cachedBootstrap = bootstrap;
      this.cachedOverview = bootstrap;
      this.cachedAchievements = bootstrap?.Achievements || [];
      this.cachedDailyQuiz = bootstrap?.DailyQuiz;
      this.cacheTime = Date.now();
    }));
  }

  getOverview(): Observable<GamificationBootstrap> {
    if (this.isCacheValid() && this.cachedOverview) {
      return of(this.cachedOverview);
    }

    return this.http.get<GamificationBootstrap>(`${this.gamificationApi}/overview`).pipe(
      tap(overview => {
        this.cachedOverview = overview;
        this.cacheTime = Date.now();
      })
    );
  }

  getAchievements(): Observable<GamificationAchievement[]> {
    if (this.isCacheValid() && this.cachedAchievements) {
      return of(this.cachedAchievements);
    }

    return this.http.get<GamificationAchievement[]>(`${this.gamificationApi}/achievements`).pipe(
      tap(achievements => {
        this.cachedAchievements = achievements?.length ? achievements : undefined;
        if (this.cachedAchievements) {
          this.cacheTime = Date.now();
        }
      })
    );
  }

  getDailyQuizStatus(): Observable<DailyQuizStatusResult> {
    if (this.isCacheValid() && this.cachedDailyQuiz) {
      return of(this.cachedDailyQuiz);
    }

    return this.http.get<DailyQuizStatusResult>(`${this.quizApi}/daily/status`).pipe(
      tap(dailyQuiz => {
        this.cachedDailyQuiz = dailyQuiz?.IsSuccess && dailyQuiz.IsAvailable ? dailyQuiz : undefined;
        if (this.cachedDailyQuiz) {
          this.cacheTime = Date.now();
        }
      })
    );
  }

  reprocessStudent(studentId: number): Observable<GamificationRecalculationResult> {
    return this.http.post<GamificationRecalculationResult>(
      `${this.gamificationApi}/admin/students/${studentId}/reprocess`,
      {}
    ).pipe(tap(() => this.invalidate()));
  }

  deleteDailyQuiz(dailyQuizId: number): Observable<DeleteDailyQuizResult> {
    return this.http.delete<DeleteDailyQuizResult>(
      `${this.gamificationApi}/admin/daily-quizzes/${dailyQuizId}`
    ).pipe(tap(() => this.invalidate()));
  }

  invalidate(): void {
    this.cachedBootstrap = undefined;
    this.cachedOverview = undefined;
    this.cachedAchievements = undefined;
    this.cachedDailyQuiz = undefined;
    this.cacheTime = 0;
  }

  private isCacheValid(): boolean {
    return this.cacheTime > 0 && Date.now() - this.cacheTime < GamificationRepositoryService.CACHE_DURATION_MS;
  }
}
